import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { ConfirmDelete, FieldLabel, PageHeader } from "@/admin/ui";
import { useAdminCategories, useRefresh } from "@/admin/hooks";

const EMPTY_CAT = { name: "", description: "", seo_title: "", seo_description: "", noindex: false };

const CategoryDialog = ({ state, onClose, nextOrder }) => {
  const [f, setF] = useState(EMPTY_CAT);
  const refresh = useRefresh();
  useEffect(() => { if (state) setF(state.cat ? { ...EMPTY_CAT, ...state.cat } : EMPTY_CAT); }, [state]);
  const save = useMutation({
    mutationFn: () => (state.cat ? api.put(`/admin/categories/${state.cat.id}`, { ...f, order: state.cat.order }) : api.post("/admin/categories", { ...f, order: nextOrder })),
    onSuccess: () => { toast.success("Sección guardada"); refresh(); onClose(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{state?.cat ? "Editar sección" : "Nueva sección"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><FieldLabel>Nombre</FieldLabel><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ej.: Energía" data-testid="category-name-input" /></div>
          <div><FieldLabel>Descripción (aparece en la web y en Google)</FieldLabel><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="category-description-input" /></div>
          <div className="border-t border-border pt-4 space-y-4">
            <p className="kicker text-[10px] text-muted-foreground">SEO de la sección (opcional)</p>
            <div><FieldLabel hint={`${(f.seo_title || "").length}/60`}>Título SEO</FieldLabel><Input value={f.seo_title} onChange={(e) => setF({ ...f, seo_title: e.target.value })} placeholder={`${f.name || "Sección"}: últimas noticias y análisis`} data-testid="category-seo-title-input" /></div>
            <div><FieldLabel hint={`${(f.seo_description || "").length}/155`}>Meta descripción</FieldLabel><Textarea rows={2} value={f.seo_description} onChange={(e) => setF({ ...f, seo_description: e.target.value })} data-testid="category-seo-description-input" /></div>
            <label className="flex items-center justify-between gap-3 border border-border p-3 text-sm">Ocultar esta sección en buscadores<Switch checked={!!f.noindex} onCheckedChange={(v) => setF({ ...f, noindex: v })} data-testid="category-noindex-switch" /></label>
          </div>
          <Button className="w-full" onClick={() => save.mutate()} disabled={save.isPending} data-testid="category-save-btn">{save.isPending && <Loader2 className="animate-spin" />}Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Categories() {
  const { data } = useAdminCategories();
  const refresh = useRefresh();
  const [dialog, setDialog] = useState(null);
  const remove = useMutation({ mutationFn: (c) => api.delete(`/admin/categories/${c.id}`), onSuccess: () => { toast.success("Sección eliminada"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const move = useMutation({
    mutationFn: async ([a, b]) => {
      await api.put(`/admin/categories/${a.id}`, { ...a, order: b.order });
      await api.put(`/admin/categories/${b.id}`, { ...b, order: a.order });
    },
    onSuccess: refresh,
  });
  const list = (data || []).map((c, i) => ({ ...c, order: i }));

  return (
    <div data-testid="admin-categories-page">
      <PageHeader kicker="Estructura" title="Secciones" description="Las secciones del menú de tu periódico. Usa las flechas para cambiar el orden.">
        <Button onClick={() => setDialog({ cat: null })} data-testid="categories-new-btn"><Plus /> Nueva sección</Button>
      </PageHeader>
      <div className="bg-card border border-border">
        {list.map((c, i) => (
          <div key={c.id} className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0" data-testid={`category-row-${c.slug}`}>
            <div className="flex flex-col">
              <button disabled={!i} onClick={() => move.mutate([c, list[i - 1]])} className="disabled:opacity-20 hover:text-primary" aria-label="Subir" data-testid={`category-up-${c.slug}`}><ArrowUp className="h-4 w-4" /></button>
              <button disabled={i === list.length - 1} onClick={() => move.mutate([c, list[i + 1]])} className="disabled:opacity-20 hover:text-primary" aria-label="Bajar" data-testid={`category-down-${c.slug}`}><ArrowDown className="h-4 w-4" /></button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-lg">{c.name} <span className="font-mono text-xs text-muted-foreground font-normal">/seccion/{c.slug}</span></p>
              <p className="text-sm text-muted-foreground truncate">{c.description}</p>
            </div>
            <span className="text-xs text-muted-foreground w-20 text-right">{c.count} {c.count === 1 ? "noticia" : "noticias"}</span>
            <Button size="icon" variant="ghost" onClick={() => setDialog({ cat: c })} aria-label="Editar" data-testid={`category-edit-${c.slug}`}><Pencil /></Button>
            <ConfirmDelete title={`¿Eliminar ${c.name}?`} description="Solo se puede eliminar si no tiene noticias." onConfirm={() => remove.mutate(c)}>
              <Button size="icon" variant="ghost" className="hover:text-destructive" aria-label="Eliminar" data-testid={`category-delete-${c.slug}`}><Trash2 /></Button>
            </ConfirmDelete>
          </div>
        ))}
      </div>
      <CategoryDialog state={dialog} onClose={() => setDialog(null)} nextOrder={list.length} />
    </div>
  );
}
